import useReportRouteChangeEvent from '@/modules/core/tracking/hooks/useReportRouteChangeEvent';
import BoostedOffers from '@/modules/shop/components/boostedOffers/BoostedOffers';
import { CashbackCapMinSpendInfo } from '@/modules/shop/components/CashbackCapMinSpendInfo';
import FeaturedArticles from '@/modules/shop/components/featuredArticles/FeaturedArticles';
import FeaturedOffers from '@/modules/shop/components/featuredOffers/FeaturedOffers';
import GiftCardStores from '@/modules/shop/components/giftCardStores/giftCardStores';
import PersonalisedStores from '@/modules/shop/components/personalisedStores/PersonalisedStores';
import PopularStores from '@/modules/shop/components/popularStores/PopularStores';
import SecondaryBanners from '@/modules/shop/components/secondaryBanners/SecondaryBanners';
import ShopBanners from '@/modules/shop/components/shopBanners/ShopBanners';
import TrendingStores from '@/modules/shop/components/trendingStores/trendingStores';
import { Article } from '@/modules/shop/models/article';
import {
  Offer,
  OfferBanner,
  PersonalisedOfferResponseModel,
  PersonalisedStoreResponseModel,
  SecondaryBanner,
  Store,
  StoreModel
} from '@/modules/shop/models/offerModels';
import FavouriteStoreProvider from '@/modules/shop/providers/FavouriteStoresProvider';

export interface ShopProps {
  featuredOffersResponse: PersonalisedOfferResponseModel | null;
  boostedCashbackOffers: Offer[] | null;
  popularStores: Store[] | null;
  trendingStores: StoreModel[] | null;
  personalisedStoresResponse: PersonalisedStoreResponseModel | null;
  giftCardStores: StoreModel[] | null;
  secondaryBanners: SecondaryBanner[] | null;
  featuredArticles: Article[] | null;
  offerBanners: OfferBanner[] | null;
}

const Shop = ({
  featuredOffersResponse,
  popularStores,
  boostedCashbackOffers,
  personalisedStoresResponse,
  giftCardStores,
  trendingStores,
  secondaryBanners,
  offerBanners,
  featuredArticles
}: ShopProps) => {
  useReportRouteChangeEvent();

  return (
    <FavouriteStoreProvider>
      <div className='col-span-full mt-[16px] flex flex-col text-cr-body-md-400 lg:mt-[54px]'>
        <div
          className='mx-auto mb-8 w-full max-w-screen-2xl lg:px-8'
          data-test-id='hero-banner'
        >
          <ShopBanners banners={offerBanners ?? []} />
        </div>

        {featuredOffersResponse && featuredOffersResponse.data?.length > 0 && (
          <div className='relative mb-8 block'>
            <FeaturedOffers
              offers={featuredOffersResponse?.data ?? []}
              algorithmVersion={featuredOffersResponse?.algorithmVersion}
              experimentIds={featuredOffersResponse?.experimentIds?.toString()}
            />
          </div>
        )}
        {personalisedStoresResponse &&
          personalisedStoresResponse?.data?.length > 0 && (
            <div className='relative mb-8 block'>
              <PersonalisedStores
                stores={personalisedStoresResponse?.data ?? []}
                algorithmVersion={personalisedStoresResponse?.algorithmVersion}
              />
            </div>
          )}
        <CashbackCapMinSpendInfo data-test-id='cb-info-1' />

        {popularStores && popularStores.length > 0 && (
          <div className='relative mb-8 block'>
            <PopularStores stores={popularStores} />
          </div>
        )}
        <CashbackCapMinSpendInfo data-test-id='cb-info-3' />

        {secondaryBanners?.length && (
          <div
            className='mx-auto mb-8 w-full max-w-screen-2xl px-4 md:px-6 lg:px-8'
            data-test-id='secondary-banner'
          >
            <SecondaryBanners banners={secondaryBanners ?? []} />
          </div>
        )}
        {boostedCashbackOffers && boostedCashbackOffers.length > 0 && (
          <div className='relative mb-8 block'>
            <BoostedOffers offers={boostedCashbackOffers} />
          </div>
        )}
        {giftCardStores && giftCardStores.length > 0 && (
          <div className='relative mb-8 block'>
            <GiftCardStores stores={giftCardStores} />
          </div>
        )}
        <CashbackCapMinSpendInfo data-test-id='cb-info-2' />
        {featuredArticles && featuredArticles?.length > 0 && (
          <div className='relative mb-8 block'>
            <FeaturedArticles articles={featuredArticles} />
          </div>
        )}
        {trendingStores && trendingStores.length > 0 && (
          <div className='relative mb-8 block'>
            <TrendingStores stores={trendingStores} />
          </div>
        )}
        <CashbackCapMinSpendInfo data-test-id='cb-info-4' />
      </div>
    </FavouriteStoreProvider>
  );
};

export default Shop;
